// utils/pixelBuild.ts — rakit userData pixel (split dari pixelPii.ts)
import { sha256HexBrowser } from './pixelHash';
import {
    normalizeEmailRaw, normalizePhoneRaw, normalizeNamePartsRaw,
    normalizeGeoRaw, normalizePostalRaw, normalizeCountryRaw,
} from './pixelNormalize';

export interface PixelUserDataRaw {
    email?: string;
    phone?: string;
    name?: string; // full name, akan di-split fn/ln
    city?: string;
    province?: string;
    postalCode?: string;
    country?: string; // 2-letter, default 'id'
    externalId?: string; // ID stabil (orderId / cm_external_id) — match key + dedup
}

// Build RAW userData untuk argumen init ke-3 fbq('init', id, data) — Meta auto-hash.
// (JANGAN fbq('set','userData',...) — itu khusus pixel Shopify, selalu ditolak.)
export function buildMetaUserDataRaw(src?: PixelUserDataRaw): Record<string, string> | null {
    if (!src) return null;
    const out: Record<string, string> = {};
    const em = normalizeEmailRaw(src.email);
    if (em) out.em = em;
    const ph = normalizePhoneRaw(src.phone);
    if (ph) out.ph = ph;
    const { fn, ln } = normalizeNamePartsRaw(src.name);
    if (fn) out.fn = fn;
    if (ln) out.ln = ln;
    const ct = normalizeGeoRaw(src.city);
    if (ct) out.ct = ct;
    const st = normalizeGeoRaw(src.province);
    if (st) out.st = st;
    const zp = normalizePostalRaw(src.postalCode);
    if (zp) out.zp = zp;
    const co = normalizeCountryRaw(src.country || 'id');
    if (co) out.country = co;
    const ex = String(src.externalId || '').trim().toLowerCase();
    if (ex) out.external_id = ex; // RAW — fbq auto-hash (sama hash dgn CAPI)
    return Object.keys(out).length ? out : null;
}

// Build HASHED userData untuk TikTok ttq.identify & Google gtag set user_data (async)
export async function buildHashedUserDataForTikTokGoogle(src?: PixelUserDataRaw): Promise<Record<string, string> | null> {
    if (!src) return null;
    const em = normalizeEmailRaw(src.email);
    const ph = normalizePhoneRaw(src.phone);
    const { fn, ln } = normalizeNamePartsRaw(src.name);
    const ct = normalizeGeoRaw(src.city);
    const st = normalizeGeoRaw(src.province);
    const zp = normalizePostalRaw(src.postalCode);
    const co = normalizeCountryRaw(src.country || 'id');
    const out: Record<string, string> = {};
    if (em) { const h = await sha256HexBrowser(em); if (h) out.email = h; }
    if (ph) { const h = await sha256HexBrowser(ph); if (h) out.phone_number = h; }
    if (fn) { const h = await sha256HexBrowser(fn); if (h) out.fn = h; }
    if (ln) { const h = await sha256HexBrowser(ln); if (h) out.ln = h; }
    if (ct) { const h = await sha256HexBrowser(ct); if (h) out.ct = h; }
    if (st) { const h = await sha256HexBrowser(st); if (h) out.st = h; }
    if (zp) { const h = await sha256HexBrowser(zp); if (h) out.zip = h; }
    if (co) { const h = await sha256HexBrowser(co); if (h) out.country = h; }
    const ex = String((src as PixelUserDataRaw).externalId || '').trim().toLowerCase();
    if (ex) { const h = await sha256HexBrowser(ex); if (h) out.external_id = h; }
    return Object.keys(out).length ? out : null;
}

// TikTok identify field mapping: { email, phone_number, ... } — sama dengan Google tapi key beda sedikit
// TikTok docs: ttq.identify({ email: sha256(email), phone_number: sha256(phone) })
// Google docs: gtag('set','user_data',{ email: sha256, phone_number: sha256, address:{...}})
// Fungsi di atas sudah menghasilkan format yang cocok untuk keduanya (email/phone_number/fn/ln/ct/st/zip/country).
// Untuk Google address, caller bisa restructure: { address: { first_name, last_name, city, region, postal_code, country } } kalau mau strict,
// tapi flat sha juga diterima di beberapa implementasi — kita kirim flat + address wrapper di googlePixel.
